/**
 * SAM-944 — Activity of a business at 375 px (canvas PhoneActivityPasses / Bookings / Transactions): a dropdown
 * switches the three views, the filters are capsules on one row that slides (Today, This week, This month, Custom
 * dates and two statuses), and every record is a card. Presentational: BusinessActivity.tsx keeps the data, the
 * filters and the drawers; this only draws them. Tailwind (SAM-175 freeze).
 */
import React, { useEffect, useState } from 'react';
import { PhoneListState, PhonePager } from '../../components/phone/PhoneCards';
import {
  PhoneCapsules, PhoneRowList, PhoneSearch, PhoneSelect, PhoneTitleRow, type PhoneCapsule, type PhoneRow,
} from '../../components/phone/PhoneListParts';
import { DateField } from '../../components/ui/DateTimeField';
import { useT, type MessageKey } from '../../i18n/admin';
import type { ActivityView, DayRange } from './activityViews';
import type { PhoneTime } from './activityPhone';

export const PHONE_PAGE_SIZE = 20;

const VIEWS: Array<{ k: ActivityView; label: MessageKey }> = [
  { k: 'bookings', label: 'activityPhone.view.bookings' },
  { k: 'passes', label: 'activityPhone.view.passes' },
  { k: 'transactions', label: 'activityPhone.view.transactions' },
];
const TIMES: Array<{ k: PhoneTime | 'custom'; label: MessageKey }> = [
  { k: 'today', label: 'activityPhone.chip.today' },
  { k: 'week', label: 'activityPhone.chip.week' },
  { k: 'month', label: 'activityPhone.chip.month' },
  { k: 'custom', label: 'activityPhone.chip.custom' },
];
/** The two statuses each view offers as capsules; the keys are the ones the view's status filter already uses. */
export const PHONE_STATUS_CHIPS: Record<ActivityView, Array<{ k: string; label: MessageKey }>> = {
  bookings: [{ k: 'confirmed', label: 'activityPhone.chip.confirmed' }, { k: 'cancelled', label: 'activityPhone.chip.cancelled' }],
  passes: [{ k: 'in', label: 'activityPhone.chip.checkedIn' }, { k: 'approved', label: 'activityPhone.chip.confirmed' }],
  transactions: [{ k: 'paid', label: 'activityPhone.chip.paid' }, { k: 'refunded', label: 'activityPhone.chip.refunded' }],
};
const SEARCH: Record<ActivityView, MessageKey> = {
  bookings: 'activityPhone.search.bookings', passes: 'activityPhone.search.passes', transactions: 'activityPhone.search.transactions',
};
const EMPTY: Record<ActivityView, MessageKey> = {
  bookings: 'activityPhone.empty.bookings', passes: 'activityPhone.empty.passes', transactions: 'activityPhone.empty.transactions',
};

interface Props {
  view: ActivityView;
  onView: (v: ActivityView) => void;
  query: string;
  onQuery: (q: string) => void;
  time: PhoneTime | 'custom' | null;
  onTime: (k: PhoneTime | 'custom') => void;
  /** Custom dates: the From / To fields are open. */
  customOpen: boolean;
  range: DayRange;
  onRange: (r: DayRange) => void;
  /** The status capsule that is on (one of PHONE_STATUS_CHIPS[view]), or null. */
  status: string | null;
  onStatus: (k: string | null) => void;
  rows: PhoneRow[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  /** Passes come paginated by the server; the other two views are paged here. */
  serverPage?: { page: number; limit: number; total: number; onPage: (p: number) => void };
  onNew?: () => void;
  /** The views this role may open (the same rule as the desktop's Segmented: the Front Desk gets no Transactions). Default: all three. */
  views?: ActivityView[];
}

export const BusinessActivityPhone: React.FC<Props> = ({
  view, onView, query, onQuery, time, onTime, customOpen, range, onRange, status, onStatus, rows, loading, failed, onRetry, serverPage, onNew, views,
}) => {
  const { t } = useT();
  const [localPage, setLocalPage] = useState(1);
  useEffect(() => { setLocalPage(1); }, [view, query, time, status, range.from, range.to]);

  const chips: PhoneCapsule[] = [
    ...TIMES.map((c) => ({ id: `time:${c.k}`, label: t(c.label), on: time === c.k })),
    ...PHONE_STATUS_CHIPS[view].map((c) => ({ id: `status:${c.k}`, label: t(c.label), on: status === c.k })),
  ];
  const pick = (id: string) => {
    if (id.startsWith('time:')) onTime(id.slice(5) as PhoneTime | 'custom');
    else onStatus(status === id.slice(7) ? null : id.slice(7));
  };

  const lastLocal = Math.max(1, Math.ceil(rows.length / PHONE_PAGE_SIZE));
  const page = serverPage ? serverPage.page : Math.min(localPage, lastLocal);
  const shown = serverPage ? rows : rows.slice((page - 1) * PHONE_PAGE_SIZE, page * PHONE_PAGE_SIZE);
  const pager = serverPage ?? { page, limit: PHONE_PAGE_SIZE, total: rows.length, onPage: setLocalPage };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <PhoneTitleRow title={t('activityPhone.title')} onNew={onNew} />
      <PhoneSelect
        ariaLabel={t('activityPhone.viewLabel')}
        value={view}
        display={t(VIEWS.find((v) => v.k === view)!.label)}
        groups={[{ options: VIEWS.filter((v) => !views || views.includes(v.k)).map((v) => ({ id: v.k, label: t(v.label) })) }]}
        onChange={(k) => onView(k as ActivityView)}
      />
      <PhoneSearch placeholder={t(SEARCH[view])} value={query} onChange={onQuery} />
      <PhoneCapsules ariaLabel={t('activityPhone.filtersLabel')} chips={chips} onPick={pick} />
      {customOpen && (
        <div className="grid grid-cols-2 gap-2">
          <DateField label={t('activityPhone.from')} ariaLabel={t('activityPhone.from')} value={range.from ?? ''} placeholder={t('activityPhone.pickDay')}
            onChange={(from) => onRange({ ...range, from: from || undefined })} />
          <DateField label={t('activityPhone.to')} ariaLabel={t('activityPhone.to')} value={range.to ?? ''} placeholder={t('activityPhone.pickDay')}
            onChange={(to) => onRange({ ...range, to: to || undefined })} />
        </div>
      )}
      {failed && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-solid border-[var(--line)] bg-white px-4 py-3 text-[13px] text-[var(--ink-700)]">
          {t('activityPhone.failed')}
          <button type="button" onClick={onRetry} className="h-11 cursor-pointer rounded-lg border border-solid border-[var(--line)] bg-white px-3 font-[inherit] text-[13px] font-semibold">
            {t('activityPhone.retry')}
          </button>
        </div>
      )}
      <PhoneListState loading={loading} failed={false} empty={!loading && !failed && shown.length === 0} emptyText={t(EMPTY[view])} />
      {!loading && shown.length > 0 && <PhoneRowList rows={shown} />}
      {!loading && <PhonePager page={pager.page} limit={pager.limit} total={pager.total} onPage={pager.onPage} />}
    </div>
  );
};
